<template>
  <pe-modal ref="modal" :header="headerTitle || $t('modals.previewCertificate')" width="960px" close-on-esc>
    <div class="py-4" ref="canvasRef">

      <DesignCanvas
        ref="certV2Ref"
        v-model="(prevCertificate.json as any)"
        :editable="false"
        :view="(viewing as any)"
        :replace="prevCertificate.replacableData"
        class="final-design-canvas"
      />

      <div v-if="prevCertificate.json?.settings?.page?.backSide" class="mt-4 flex items-center justify-center">
        <UiRadioButton v-model="viewing" :options="[
          { value: 'front', label: $t('labels.front') },
          { value: 'back', label: $t('labels.back') },
          { value: 'both', label: $t('labels.both') }
        ]" hideRadio />
      </div>
    </div>
    <template #footer>
      <div class="flex flex-wrap items justify-center gap-4 py-4">
        <UiDropdown>
          <UiButton type="primary" class="p-2 uppercase">
            <UiIcon icon="download" /> {{ $t('button.download') }}
          </UiButton>

          <template #content>
            <div class="flex flex-col gap-1">
              <UiButton type="ghost" icon="photo" class="p-2" @click="() => saveImage('front')">Como Imagem (Frente)
              </UiButton>
              <UiButton v-if="prevCertificate.json?.settings?.page?.backSide" type="ghost" icon="photo" class="p-2"
                @click="() => saveImage('back')">Como Imagem (Verso)</UiButton>
              <UiButton type="ghost" icon="pdf" class="p-2" @click="() => savePdf()">Como PDF</UiButton>
            </div>
          </template>
        </UiDropdown>

        <div class="flex items-center space-x-1">
          <UiButton type="primary" outline icon="brand-linkedin" :text="$t('labels.shareLinkedin')"
            class="p-2 uppercase" :loading="busy" @click="() => share()" />

          <div v-tooltip :title="$t('tooltips.certificates.linkedin')"
            class="flex items-center space-x-2 cursor-help">
            <UiIcon icon="info-circle" size="22px" />
          </div>
        </div>

        <div class="flex items-center space-x-1">
          <UiButton type="primary" outline icon="brand-linkedin" :text="$t('labels.linkLinkedin')"
            class="p-2 uppercase" :loading="busy" @click="() => addToLinkedin()" />

          <div v-tooltip :title="$t('tooltips.certificates.profile')" class="flex items-center space-x-2 cursor-help">
            <UiIcon icon="info-circle" size="22px" />
          </div>
        </div>
      </div>
    </template>
  </pe-modal>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import jsPDF from 'jspdf'
import { duration, formatter, safeCopy, dataURLtoFile } from '@/common/util'
import { useCertificadosUsuariosStore } from '@/stores'
import { versionUpgrade } from '@/services/certificados.service'

import UiButton from '@/components/ui/button.vue'
import UiDropdown from '@/components/ui/dropdown.vue'
import UiRadioButton from '@/components/ui/radioButton.vue'
import UiIcon from '@/components/ui/icon.vue'

import DesignCanvas from '@/views/certificado/components/designCanvas.vue'

import type { StructureType } from '@/views/certificado/components/types'

const certificadosUsuariosStore = useCertificadosUsuariosStore()
const { plantar } = (<any>window).configuration || { plantar: {} }

const defaultPrevCertificate = {
  tags: {},
  json: {},
  courseItems: [],
  trailItems: [],
  replacableData: {}
}

const prevCertificate = ref<{
  tags: any
  json: StructureType
  courseItems?: any
  trailItems?: any
  replacableData?: any
}>({ ...safeCopy(defaultPrevCertificate) })

const canvasRef = ref()
const isLoading = ref(true)
const busy = ref(false)

const bunnyStatus = ref('initial')
const viewing = ref('front')

const certV2Ref = ref<InstanceType<typeof DesignCanvas> | null>(null)

const certificateUser = ref<{
  credential?: string,
  title: string,
  issueDate?: Date
}>()

const props = defineProps<{
  headerTitle?: string
  certificateId?: string
  userId?: string
  type?: string
  idCourseFormation?: string
  course?: any[]
  trail?: any[]
}>()

const modal = ref()

const open = async (data?: {
  jsonCertificado: string | {}
  cargaHoraria?: number
  dataEmissao?: string
  title?: string
  nomeUsuario?: string
  cpf?: string
  cargo?: string
  dataInscricao?: string
  course?: any[]
  trail?: any[]
  url?: string
  credential?: string
}) => {
  if (!data) return

  isLoading.value = true
  viewing.value = 'front'

  prevCertificate.value.json = typeof data.jsonCertificado === 'string' ? JSON.parse(data.jsonCertificado) : data.jsonCertificado

  certificateUser.value = {
    title: data?.title || null,
    issueDate: new Date(data.dataEmissao)
  }

  prevCertificate.value.json = versionUpgrade(prevCertificate.value.json)

  prevCertificate.value.replacableData = {
    '#TITULO#': data?.title || '',
    '#NOME#': data?.nomeUsuario || '',
    '#CPF#': formatter.cpfCnpj(data.cpf) || '',
    '#CARGO#': data?.cargo || '',
    '#DT_CONCLUSAO#': data.dataEmissao ? formatter.date(data?.dataEmissao) : null,
    '#CARGA_HORARIA#': data?.cargaHoraria ? duration(data.cargaHoraria) : '',
    '#DT_INSCRICAO#': data?.dataInscricao
      ? formatter.date(data?.dataInscricao)
      : null,
    '#CREDENCIAL#': data?.credential || ''
  }

  // Geração de conteúdo para #CONTENT#, #CONTENT2#, ...
  // Prioriza trilha, se não houver, usa cursos
  if (data.trail?.length) {
    const lines: string[] = data.trail.flatMap((item) => {
      const trailLines: string[] = []
      trailLines.push(`- ${item.name}${item.duration && item.duration !== '00:00:00' ? ` - (${item.duration})` : ''};\n`)
      if (item?.items?.length) {
        item.items.forEach((course) => {
          trailLines.push(`\t\t\t\t• ${course.name}${course.duration && course.duration !== '00:00:00' ? ` - (${course.duration})` : ''};\n`)
          if (course?.items?.length) {
            course.items.forEach((subItem) => {
              trailLines.push(`\t\t\t\t\t\t\t◦ ${subItem.name}${subItem.duration && subItem.duration !== '00:00:00' ? ` - (${subItem.duration})` : ''};\n`)
            })
          }
        })
      }
      return trailLines
    })
    const chunkSize = 26
    const chunks = []
    for (let i = 0; i < lines.length; i += chunkSize) {
      chunks.push(lines.slice(i, i + chunkSize).join(''))
    }
    chunks.forEach((chunk, idx) => {
      prevCertificate.value.replacableData[`#CONTENT${idx === 0 ? '' : idx + 1}#`] = chunk
    })
  } else if (data.course?.length) {
    const courseLines: string[] = data.course.map((item) => {
      return `• ${item.name}${item.duration !== '00:00:00' ? ` - (${item.duration})` : ''};\n`
    })
    const chunkSize = 26
    const courseChunks = []
    for (let i = 0; i < courseLines.length; i += chunkSize) {
      courseChunks.push(courseLines.slice(i, i + chunkSize).join(''))
    }
    courseChunks.forEach((chunk, idx) => {
      prevCertificate.value.replacableData[`#CONTENT${idx === 0 ? '' : idx + 1}#`] = chunk
    })
  }

  // Processar as camadas do certificado (QR code)
  const processLayers = (layers: any[]) => {
    if (!layers) return []

    // Process QR code layers
    return layers.map((layer: any) => {
      if (layer.type !== 'object' || layer.object?.type !== 'qrcode') return layer

      return {
        ...layer,
        object: {
          ...layer.object,
          url: data.url
        }
      }
    })
  }

  prevCertificate.value.json.layers = processLayers(prevCertificate.value.json.layers);

  // @ts-ignore
  if (prevCertificate.value.json?.backLayers) {
    // @ts-ignore
    prevCertificate.value.json.backLayers = processLayers(prevCertificate.value.json.backLayers);
  }

  await modal.value?.open()

  if (!canvasRef.value) {
    isLoading.value = false
    return
  }
}

const saveImage = (page?: string) => {
  const dataURL = canvasRef.value?.getElementsByTagName('canvas')[page === 'front' ? 0 : 1].toDataURL()

  var link = document.createElement('a')
  link.download = 'certificado.png'
  link.href = dataURL
  link.click()
}

const prepareForLinkedin = () => {
  bunnyStatus.value = 'loading'

  const dataURL = canvasRef.value?.getElementsByTagName('canvas')[0].toDataURL()
  const file = dataURLtoFile(dataURL, 'certificado.png')

  return certificadosUsuariosStore.create(
    props.certificateId,
    props.userId,
    props.type,
    props.idCourseFormation,
    file
  ).then((res) => {
    bunnyStatus.value = 'success'
    return res
  }).catch(() => {
    bunnyStatus.value = 'error'
  }).finally(() => {
    isLoading.value = false
  })
}

const share = () => {
  busy.value = true

  prepareForLinkedin().then((result) => {
    if (result) {
      const linkedInUrlBase = (window as any).configuration.linkedIn.host
      const plantarUrlBase = [plantar.host, plantar.pathV1].join('/')

      const url = `${linkedInUrlBase}/shareArticle/?mini=true&url=${plantarUrlBase}/certificadousuario/${result.credencial}/public`
      window.open(url, '_blank').focus()
    }
  }).finally(() => {
    busy.value = false
  })
}

const addToLinkedin = () => {
  busy.value = true

  prepareForLinkedin().then((result) => {
    if (result) {
      const issueMonth = certificateUser.value.issueDate.getMonth() + 1
      const issueYear = certificateUser.value.issueDate.getFullYear()

      const linkedInUrlBase = (window as any).configuration.linkedIn.host
      const plantarUrlBase = [plantar.host, plantar.pathV1].join('/')
      const certificateUrl = `${plantarUrlBase}/certificadousuario/${result.credencial}/public`

      const url = `${linkedInUrlBase}/profile/add?startTask=${certificateUser.value.title}&name=${certificateUser.value.title}&issueYear=${issueYear}&issueMonth=${issueMonth}&certUrl=${certificateUrl}&certId=${result.credencial}`
      window.open(url, '_blank').focus()
    }
  }).finally(() => {
    busy.value = false
  })
}

const savePdf = () => {
  const pdf = new jsPDF('l', 'mm', 'a4')

  const imgs = [
    new Promise((resolve: any) => {
      const img = new Image()
      img.src = canvasRef.value?.getElementsByTagName('canvas')[0].toDataURL()

      img.onload = () => {
        pdf.addImage(img, 'PNG', 0, 0, 297, 210)
        resolve()
      }
    }),
    new Promise((resolve: any) => {
      if (!prevCertificate.value.json?.settings?.page?.backSide || !canvasRef.value?.getElementsByTagName('canvas')?.[1]) return resolve()

      const img = new Image()
      img.src = canvasRef.value?.getElementsByTagName('canvas')[1].toDataURL()

      img.onload = () => {
        pdf.addPage()
        pdf.addImage(img, 'PNG', 0, 0, 297, 210)
        resolve()
      }
    })
  ]

  Promise.all(imgs).then(() => {
    pdf.save('certificado.pdf')
  })
}

defineExpose({
  open
})
</script>

<style lang="scss">
.final-design-canvas canvas {
  width: 100% !important;
  height: auto !important;
  overflow: hidden;
  position: relative;
}
</style>